import { properties } from "../common/properties";
import { v4 as uuidv4 } from "uuid";

// Store for getCaptchaSiteKey result
let storedSiteConfig: {
  siteKey: string;
  fullMatchedDomain: string;
  headerUrl: string;
  footerUrl: string;
} | null = null;

export const createForgotPasswordAPIBody = (
  email: string,
  token: string,
  baseURL: string,
  targetUrl: any,
  locale: string
) => {
  const apiBody: forgotPasswordPayload = {
    email: email,
    captchaToken: token,
    targetUrl: targetUrl ? targetUrl : `${baseURL}/${locale}/login`,
  };
  return apiBody;
};

export const fetchHtml = async (url: string): Promise<string> => {
  if (!url) return "";
  try {
    const res = await fetch(url);
    return await res.text();
  } catch (e) {
    console.error("Failed to fetch html from " + url, e);
    return "";
  }
};

export type FragmentAssets = {
  bodyHtml: string;
  scripts: HTMLScriptElement[];
  styleHrefs: string[];
  styleTags: string[];
};

export const parseFragmentHtml = (html: string): FragmentAssets => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const scripts = Array.from(
    doc.querySelectorAll("script")
  ) as HTMLScriptElement[];
  const styleHrefs = Array.from(
    doc.querySelectorAll('link[rel="stylesheet"][href]')
  ).map((link) => (link as HTMLLinkElement).href);
  const styleTags = Array.from(doc.querySelectorAll("style")).map(
    (style) => style.innerHTML
  );
  return { bodyHtml: doc.body.innerHTML, scripts, styleHrefs, styleTags };
};

const injectedStylesheetPromises = new Map<string, Promise<void>>();
const injectedInlineStyles = new Set<string>();

// Injects fragment stylesheets/inline styles into <head> (deduped) and resolves
// once every external stylesheet has loaded (or failed) - or after fallbackMs,
// whichever comes first, so a stuck/blocked resource can never leave the caller
// waiting forever (and the header/footer permanently blank).
export const injectFragmentStyles = (
  styleHrefs: string[],
  styleTags: string[],
  fallbackMs = 4000
): Promise<void> => {
  styleTags.forEach((css) => {
    if (!css || injectedInlineStyles.has(css)) return;
    injectedInlineStyles.add(css);
    const styleEl = document.createElement("style");
    styleEl.innerHTML = css;
    document.head.appendChild(styleEl);
  });

  const pending = styleHrefs
    .filter((href) => href)
    .map((href) => {
      let stylesheetPromise = injectedStylesheetPromises.get(href);
      if (!stylesheetPromise) {
        stylesheetPromise = new Promise<void>((resolve) => {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = href;
          link.onload = () => resolve();
          link.onerror = () => resolve();
          document.head.appendChild(link);
        });
        injectedStylesheetPromises.set(href, stylesheetPromise);
      }
      return stylesheetPromise;
    });

  if (pending.length === 0) return Promise.resolve();

  return Promise.race([
    Promise.all(pending).then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, fallbackMs)),
  ]);
};

export const fetchForgotPasswordAPI = async (payload: object) => {
  const uuid = uuidv4();
  try {
    const result = await getCaptchaSiteKey();
    const fullMatchedDomain = result?.fullMatchedDomain || "";
    const apiEndpoint =
      properties.forgotPasswordEndpointTemplate.replace(
        "%domain%",
        fullMatchedDomain
      ) ?? properties.apiEndpoint;
    await fetch(apiEndpoint, {
      method: "POST",
      headers: {
        "x-request-id": uuid,
      },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    console.log("Error has occured while calling the API: " + e);
  }
};

export const createResetPasswordPayload = (
  password: string,
  encryptedData: string,
  captchaToken: string
) => {
  const payload: resetPasswordPayload = {
    password: password,
    encryptedData: encryptedData,
    captchaToken: captchaToken,
  };
  return payload;
};

export const resetPasswordAction = async (payload: resetPasswordPayload) => {
  const uuid = uuidv4();
  try {
    const result = await getCaptchaSiteKey();
    const fullMatchedDomain = result?.fullMatchedDomain || "";
    const resetPasswordApiEndpoint =
      properties.resetPasswordEndpointTemplate.replace(
        "%domain%",
        fullMatchedDomain
      ) ?? properties.resetPasswordApiEndpoint;
    const response = await fetch(resetPasswordApiEndpoint, {
      method: "POST",
      headers: {
        "x-request-id": uuid,
      },
      body: JSON.stringify(payload),
    });
    const responseData = await response.json();
    return responseData;
  } catch (e) {
    console.log("Failed to send reset password request " + e);
    throw e;
  }
};

export const getCaptchaSiteKey = async (): Promise<{
  siteKey: string;
  fullMatchedDomain: string;
  headerUrl: string;
  footerUrl: string;
}> => {
  // Return cached result if available
  if (storedSiteConfig) {
    return storedSiteConfig;
  }

  const { hostname } = window.location;
  const encodedDomain = btoa(hostname);
  const fullMatchedDomain = `https://${hostname}`;
  try {
    const res = await fetch(
      `${properties.siteConfigApiUrl}?domain=${encodedDomain}`
    );
    const config = await res.json();
    const siteId = Object.keys(config)[0];
    const siteKey = config[siteId]?.siteKey;
    const headerUrl = config[siteId]?.headerUrl;
    const footerUrl = config[siteId]?.footerUrl;

    // Fall back to env-configured URLs for any field the API didn't provide
    storedSiteConfig = {
      siteKey: siteKey || properties.siteKey,
      fullMatchedDomain,
      headerUrl: headerUrl || properties.headerFragmentUrl,
      footerUrl: footerUrl || properties.footerFragmentUrl,
    };

    return storedSiteConfig;
  } catch (err) {
    console.log("Failed to fetch site config:", err);
    // Site-config call failed entirely — fall back to env-configured URLs
    return {
      siteKey: properties.siteKey,
      fullMatchedDomain,
      headerUrl: properties.headerFragmentUrl,
      footerUrl: properties.footerFragmentUrl,
    };
  }
};
